'use client';

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper/modules";
import 'swiper/css';
import 'swiper/css/navigation';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faLayerGroup, faTag, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { Collection, fetchCollections } from "@/lib/collections";
import { ArtworkModal, type ArtworkModalItem } from "@/components/sections/ArtworkModal";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  mapCollectionToArtworkModalItems
} from "@/components/sections/utils/collectionArtworks";

interface CollectionShowroomProps {
  collection: Collection;
}

export function CollectionShowroom({ collection }: CollectionShowroomProps) {
  const artworkItems = useMemo(
    () => mapCollectionToArtworkModalItems(collection),
    [collection]
  );

  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkModalItem | null>(null);
  const [relatedCollections, setRelatedCollections] = useState<Collection[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(true);

  // Carregar coleções semelhantes
  useEffect(() => {
    const loadRelatedCollections = async () => {
      try {
        const result = await fetchCollections(1, 20);
        // Filtrar a coleção atual e pegar até 8 coleções semelhantes
        const filtered = result.collections
          .filter((c) => c.id !== collection.id)
          .slice(0, 8);
        setRelatedCollections(filtered);
      } catch (error) {
        console.error('Failed to fetch related collections:', error);
      } finally {
        setIsLoadingRelated(false);
      }
    };

    loadRelatedCollections();
  }, [collection.id]);

  const handleArtworkClick = (artwork: ArtworkModalItem) => {
    setSelectedArtwork(artwork);
  };

  return (
    <div className="flex flex-col gap-16 pb-20">
      {/* Top collection info: back button, title, description, tags and badges */}
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-16 mb-8 lg:mb-12">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <Link
              href="/colecoes"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-white/70 transition hover:border-white/30 hover:text-white"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
              Voltar
            </Link>

            <h1 className="ml-2 text-2xl font-semibold leading-tight text-white sm:text-3xl lg:text-4xl">
              {collection.title}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white/70">
              <FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5" />
              {collection.assetsCount} assets
            </span>
            {collection.isPremium && (
              <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/40 bg-yellow-400/15 px-3 py-1 text-xs font-semibold text-yellow-300">
                Premium
              </span>
            )}
          </div>
        </div>

        {collection.description && (
          <p className="mt-3 text-base leading-relaxed text-white/70 sm:text-lg">{collection.description}</p>
        )}

        {(collection.tags?.length ?? 0) > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {collection.tags.slice(0, 6).map((tag) => (
              <span
                key={`${collection.id}-top-tag-${tag}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand-secondary/40 bg-brand-secondary/20 px-3 py-1 text-[11px] font-medium text-brand-secondary"
              >
                <FontAwesomeIcon icon={faTag} className="h-3 w-3" />
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Full gallery section - visible on all screen sizes */}
      <section className="space-y-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-white">Galeria da coleção</h2>
            <p className="text-sm text-white/60">
              Clique em qualquer arte para abrir a visualização completa com detalhes e opções de download.
            </p>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white/60">
            {artworkItems.length.toString().padStart(2, "0")} artes
          </span>
        </div>

        {artworkItems.length === 0 ? (
          <EmptyState
            icon={faLayerGroup}
            title="Nenhuma arte nesta coleção"
            description="Marque artes como imagens de coleção no painel administrativo para que elas apareçam aqui."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {artworkItems.map((artwork, index) => (
              <button
                key={`${collection.slug}-grid-${artwork.id}`}
                type="button"
                onClick={() => handleArtworkClick(artwork)}
                className="group relative aspect-[4/5] overflow-hidden rounded-[0.5rem] border border-white/10 bg-black/25 transition duration-200 hover:-translate-y-1 hover:border-brand-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60"
              >
                <Image
                  src={artwork.image}
                  alt={`Arte ${index + 1} da coleção ${collection.title}`}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  className="object-cover transition duration-500 group-hover:scale-105"
                  unoptimized
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/60 rounded-[.5rem]" />
                {artwork.format !== "PSD" && (
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 p-4 text-left text-white">
                    <span className="line-clamp-2 text-sm font-semibold">{artwork.title}</span>
                    <span className="rounded-full border border-white/20 bg-black/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                      {artwork.format}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Coleções Semelhantes */}
      {!isLoadingRelated && relatedCollections.length > 0 && (
        <section className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-white">Coleções semelhantes</h2>
              <p className="mt-1 text-sm text-white/60">
                Explore outras coleções que podem te interessar.
              </p>
            </div>
            <Link
              href="/colecoes"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-secondary transition-transform hover:translate-x-1 hover:text-white"
            >
              Ver todas as coleções
              <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
            </Link>
          </div>

          <div className="relative overflow-hidden">
            <Swiper
              modules={[Navigation, A11y]}
              navigation
              spaceBetween={12}
              slidesPerView="auto"
            >
              {relatedCollections.map((relatedCollection) => (
                <SwiperSlide key={relatedCollection.id} className="!h-auto !w-auto">
                  <Link href={`/colecoes/${relatedCollection.slug}`} className="relative w-[140px] sm:w-[160px] lg:w-[180px] block">
                    <div className="group relative overflow-hidden rounded-[12px] border border-white/10 bg-white/5 p-0.5 shadow-[0_18px_60px_-30px_rgba(15,0,36,0.8)] transition-transform hover:-translate-y-1">
                      <div className="grid grid-cols-2 gap-0.5">
                        {relatedCollection.previews.slice(0, 4).map((preview, idx) => (
                          <div
                            key={`${relatedCollection.id}-preview-${idx}`}
                            className="relative aspect-square overflow-hidden rounded-sm"
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src={preview}
                              alt={`Prévia da coleção ${relatedCollection.title}`}
                              className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/35 opacity-0 transition-opacity duration-300 group-hover:opacity-60" />
                          </div>
                        ))}
                      </div>
                      <div className="pointer-events-none absolute inset-0 flex items-end justify-end p-2">
                        <span className="rounded-full bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.25em] text-white">
                          {relatedCollection.assetsCount} assets
                        </span>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>
      )}

      {selectedArtwork && (
        <ArtworkModal
          artwork={selectedArtwork}
          relatedArtworks={artworkItems.filter((item) => item.id !== selectedArtwork.id)}
          onClose={() => setSelectedArtwork(null)}
          onSwitchArtwork={(art) => setSelectedArtwork(art)}
        />
      )}
    </div>
  );
}

export default CollectionShowroom;
